import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { formatCZK } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Check } from "lucide-react";
import { toast } from "sonner";

const SUBTITLES = {
  zaklad: "Základ pro malou partu",
  stavba: "Nejoblíbenější volba pro rostoucí firmy",
  firma: "Bez limitů pro velké firmy",
};
const plural = (n, one, few, many) => (n === 1 ? one : n >= 2 && n <= 4 ? few : many);

// Modal „Povýšit tarif" (zadání 126/A) — karty jen pro vyšší tarify,
// částky se počítají z /billing/upgrade-options, nikdy nejsou v kódu.
export function UpgradeDialog({ open, onOpenChange, summary, upg, onUpgraded }) {
  const options = useMemo(() => upg?.options || [], [upg]);
  const hasYear = options.some((o) => o.cycle === "year");
  const [cycle, setCycle] = useState("month");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setCycle(hasYear ? "year" : "month"); }, [open, hasYear]);

  const usage = summary?.usage;
  const u = usage?.users, p = usage?.projects, o = usage?.ocr;

  // Limity, na kterých firma je nebo přes které přetéká — jen ty jmenujeme.
  const usersAt = u?.limit != null && u.limit > 0 && u.used >= u.limit;
  const projectsAt = p?.limit != null && p.limit > 0 && p.used >= p.limit;

  const cards = useMemo(() => options.filter((x) => x.cycle === cycle), [options, cycle]);
  const monthlyPrice = (card) => (card.cycle === "year" ? card.price / 10 : card.price);
  const saving = (card) => monthlyPrice(card) * 2;
  const struck = (card) => monthlyPrice(card) * 12;
  const maxSaving = useMemo(
    () => Math.max(0, ...options.filter((x) => x.cycle === "year").map((x) => (x.price / 10) * 2)), [options]);

  // Doporučená = nejlevnější karta, která zvládne všechna překročená čerpání.
  const recommendedPlan = useMemo(() => {
    const clears = (card) =>
      (!usersAt || card.users == null || card.users > u.used) &&
      (!projectsAt || card.active_projects == null || card.active_projects > p.used);
    const sorted = [...cards].sort((a, b) => a.price - b.price);
    return (sorted.find(clears) || sorted[0])?.plan;
  }, [cards, usersAt, projectsAt, u, p]);

  const bullets = (card) => {
    const out = [];
    if (card.users == null) out.push(`Neomezeně lidí — dnes ${u?.limit ?? "bez limitu"}`);
    else if (u?.limit != null) out.push(`${card.users} lidí místo ${u.limit} — o ${card.users - u.limit} míst víc`);
    else out.push(`Až ${card.users} lidí`);
    if (card.active_projects == null) {
      out.push(p?.limit == null ? "Neomezeně zakázek i nadále"
        : `Neomezeně zakázek — dnes ${p.limit}${p.used > p.limit ? `, máte ${p.used}` : ""}`);
    } else out.push(`${card.active_projects} ${plural(card.active_projects, "zakázka", "zakázky", "zakázek")} místo ${p?.limit ?? "∞"}`);
    const curOcr = o?.limit === 0 ? "žádný" : o?.limit == null ? "neomezeně" : `${o.limit}/měs`;
    if (card.ocr_month == null) out.push(`Neomezeně dokladů přes AI — dnes ${curOcr}`);
    else out.push(`${card.ocr_month} dokladů přes AI měsíčně — dnes ${curOcr}`);
    return out;
  };

  const doUpgrade = async (card) => {
    setBusy(true);
    try {
      await api.post("/billing/upgrade", { plan: card.plan, cycle: card.cycle });
      toast.success(`Tarif povýšen na ${card.label}`);
      onOpenChange(false);
      onUpgraded?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[880px] sm:max-w-[880px] w-[calc(100%-2rem)] max-h-[90vh] overflow-y-auto rounded-[20px]" data-testid="upgrade-dialog">
        <DialogHeader>
          <DialogTitle>Povýšit tarif</DialogTitle>
          <DialogDescription className="sr-only">Výběr vyššího tarifu</DialogDescription>
        </DialogHeader>

        <div className="bg-brand/10 border border-brand/25 rounded-[14px] px-3.5 py-2.5 text-sm" data-testid="upgrade-reason">
          {usersAt || projectsAt ? (
            <>
              Máte {projectsAt && <b>{p.used} aktivních {plural(p.used, "zakázku", "zakázky", "zakázek")}</b>}
              {projectsAt && usersAt && " a "}
              {usersAt && <b>{u.used} z {u.limit} lidí</b>}.{" "}
              {projectsAt
                ? `${summary?.plan_label} zvládne ${p.limit} ${plural(p.limit, "zakázku", "zakázky", "zakázek")} — vyšší tarif limity zruší.`
                : "Vyšší tarif limity zruší."}
            </>
          ) : (
            "Vyšší tarif zvedá limity lidí, zakázek i dokladů přes AI."
          )}
        </div>

        {hasYear && (
          <div className="flex flex-col items-center gap-2">
            <div className="h-11 p-1 rounded-full bg-secondary inline-flex" data-testid="cycle-switch">
              <button type="button" onClick={() => setCycle("month")} data-testid="cycle-month"
                className={`h-full px-5 rounded-full text-sm font-bold transition-colors ${cycle === "month" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"}`}>
                Měsíčně
              </button>
              <button type="button" onClick={() => setCycle("year")} data-testid="cycle-year"
                className={`h-full px-5 rounded-full text-sm font-bold transition-colors flex items-center gap-1.5 ${cycle === "year" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"}`}>
                Ročně
                <span className="px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-500 text-[11px] font-bold">−2 měsíce</span>
              </button>
            </div>
            <p className="text-sm text-muted-foreground" data-testid="cycle-hint">
              {cycle === "year" ? `Při roční platbě ušetříte až ${formatCZK(maxSaving)}.` : "Roční platba vyjde o dva měsíce levněji."}
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
          {cards.map((card) => {
            const isRec = card.plan === recommendedPlan;
            return (
              <div key={`${card.plan}-${card.cycle}`} data-testid={`plan-card-${card.plan}`}
                className={`relative rounded-[16px] bg-card p-4 ${isRec ? "border-2 border-brand" : "border border-border"}`}>
                {isRec && (
                  <span className="absolute -top-[11px] left-[17px] bg-brand text-white text-[11px] font-bold px-2.5 py-[3px] rounded-full" data-testid={`plan-recommended-${card.plan}`}>
                    Doporučeno pro vás
                  </span>
                )}
                <div className="text-lg font-black">{card.label}</div>
                <div className="text-sm text-muted-foreground">{SUBTITLES[card.plan] || ""}</div>
                <div className="mt-2 flex items-baseline gap-1.5">
                  <span className="text-[27px] md:text-[31px] font-black tabular-nums whitespace-nowrap" data-testid={`plan-price-${card.plan}`}>{formatCZK(card.price)}</span>
                  <span className="text-sm text-muted-foreground">/{card.cycle === "year" ? "rok" : "měsíc"}</span>
                </div>
                {card.cycle === "year" && (
                  <div className="mt-1 flex flex-wrap items-center gap-1.5">
                    <span className="text-sm text-muted-foreground line-through tabular-nums" data-testid={`plan-struck-${card.plan}`}>{formatCZK(struck(card))}</span>
                    <span className="px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-500 text-[11px] font-bold">2 měsíce zdarma</span>
                    <span className="px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-500 text-[11px] font-bold" data-testid={`plan-saving-${card.plan}`}>ušetříte {formatCZK(saving(card))}</span>
                  </div>
                )}
                {card.setup_fee_diff > 0 && (
                  <div className="mt-1.5 text-sm text-muted-foreground" data-testid={`plan-setup-fee-${card.plan}`}>
                    + jednorázový doplatek za implementaci <b className="text-foreground whitespace-nowrap tabular-nums">{formatCZK(card.setup_fee_diff)}</b> bez DPH
                  </div>
                )}
                <div className="mt-3 text-xs font-bold uppercase tracking-widest text-muted-foreground">Co získáte navíc</div>
                <ul className="mt-1.5 space-y-1.5">
                  {bullets(card).map((b, i) => (
                    <li key={i} className="flex items-start gap-1.5 text-sm">
                      <Check size={15} className="text-emerald-500 mt-0.5 shrink-0" /> <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <Button disabled={busy} onClick={() => doUpgrade(card)} data-testid={`plan-cta-${card.plan}`}
                  className={`mt-4 w-full h-11 font-bold ${isRec ? "bg-brand text-white hover:bg-brand/90" : "bg-secondary text-foreground hover:bg-secondary/80"}`}>
                  {busy ? "Měním…" : `Přejít na ${card.label}${card.cycle === "year" ? " ročně" : ""}`}
                </Button>
              </div>
            );
          })}
        </div>

        <p className="text-xs text-muted-foreground text-center" data-testid="upgrade-footer">
          Změna platí okamžitě, rozdíl za zbytek období Stripe dopočítá a strhne z karty.<br />
          Doplatek za implementaci (rozdíl oproti vašemu tarifu) vám vyfakturujeme zvlášť.<br />
          Snížení tarifu vyřídíme osobně: +420 728 398 282
        </p>
      </DialogContent>
    </Dialog>
  );
}
